import { Component } from 'preact';
import { motion } from 'framer-motion';
import ChatView from './ChatView';
import VikingOrnament from './VikingOrnament';
import CardPickerPanel from './CardPickerPanel';
import { ART_GROUP_BASE } from '../utils/sorcery/artGroups';
import { playUI, UI } from '../utils/arena/uiSounds';
import { notificationSoundEnabled } from '../utils/arena/soundSettings';
import { GOLD, TEXT_PRIMARY, TEXT_BODY, TEXT_MUTED, OPAQUE_TEXTURED_PANEL_STYLE, FourCorners } from '../lib/medievalTheme';

// Standalone chat panel. Opens when the user clicks a friend in
// SocialPanel and lives independently. Its own position, its own
// close button, no backdrop. Two panels can be open at once:
// SocialPanel right-docked, ChatPanel docked just left of it,
// mirroring Discord's friend list + active DM layout.
export default class ChatPanel extends Component {
  // `attachVersion` is bumped whenever ChatView's attach list changes
  // so the card picker re-renders with the updated counts (the picker
  // reads counts from chatViewRef.state.attachCards, which isn't
  // observable from React on its own).
  state = { showCardPicker: false, attachVersion: 0 };

  componentDidUpdate(prevProps) {
    // Route incoming chat WS events to the active chat view. The
    // server pushes a new message on props.lastChatMessage and we
    // forward it to the child ChatView if it's for the friend we're
    // currently chatting with. Same pattern the old Mailbox used
    // before chat moved into its own panel.
    //
    // Important: ChatView is keyed by friendId below, so chatViewRef
    // always corresponds to the currently-mounted friend. The
    // senderId === friendId check prevents an incoming message from
    // friend A leaking into a chat we're holding open with friend B.
    const msg = this.props.lastChatMessage;
    if (msg && msg !== prevProps.lastChatMessage) {
      // Audible cue for an arriving message. This panel is always
      // mounted, so it fires for every conversation and not just the
      // one on screen. A message in a chat you don't have open is
      // exactly the one that gets missed. This is the only chime a
      // direct message gets: notificationsStore mutes the chat.message
      // feed row so the two pushes never double-ping. Guarded on the
      // sender because your own outgoing message must stay silent.
      if (msg.senderId && msg.senderId !== this.props.profile?.id && notificationSoundEnabled('chat.message')) {
        playUI(UI.NOTIFICATION_SOFT);
      }
      const friendId = this.props.friend?.friendId;
      if (this.chatViewRef && friendId === msg.senderId) {
        this.chatViewRef.receiveMessage(msg);
      }
    }
    // Close the card picker when the chat friend changes. The picker
    // is attached to one chat's attach list. Keeping it open against
    // a different conversation would let the user add cards to the
    // wrong message and is generally confusing.
    const prevFriendId = prevProps.friend?.friendId;
    const nextFriendId = this.props.friend?.friendId;
    if (nextFriendId !== prevFriendId && this.state.showCardPicker) {
      this.setState({ showCardPicker: false });
    }
  }

  render() {
    const {
      open, friend, profile, sorceryCards,
      onClose, onViewProfile, onProfileReload,
      onAcceptMatchInvite, onDeclineMatchInvite, onRejoinMatch, onProfileUpdate,
      onAcceptDraftInvite,
      socialOpen,
    } = this.props;

    // Slide in from the right edge when the social panel isn't open, or
    // from just next to it when it is. All offsets in cqw so the lateral
    // chain (picker ← chat ← Mailbox) stays in lockstep with the
    // cqw-sized panels at every canvas size. No overlap on large
    // canvas, no oversized gaps on small canvas.
    // Mailbox: width 23.75cqw, right 1cqw. ChatPanel: width 25cqw,
    // gap 0.5cqw. Picker: width 25cqw, gap 0.5cqw.
    const rightOffset = socialOpen ? '25.25cqw' : '1cqw';
    const pickerRightOffset = socialOpen ? '50.75cqw' : '26.5cqw';
    const attachCards = this.chatViewRef?.state?.attachCards || [];

    // Panel always mounted so ChatView (and its scroll, draft message,
    // attach list, fetched history) persists across every close/reopen
    // of the social overlay. Visibility is animated via opacity + x,
    // not AnimatePresence mount/unmount. ChatView itself renders only
    // when `friend` is set, and is keyed by friend.friendId so
    // switching to a different conversation remounts ChatView with
    // fresh state. The remount is required for correctness. Without
    // it, an in-flight fetchMessages() for the previous friend would
    // resolve into the new friend's state and pollute its localStorage
    // cache (messages from one chat appearing in another).
    const visible = open && !!friend;
    return (
      <>
        <motion.div
          className="fixed z-[61] flex flex-col"
          data-screenshot-ignore="true"
          initial={false}
          animate={{
            opacity: visible ? 1 : 0,
            x: visible ? 0 : 16,
            y: '-50%',
          }}
          transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
          style={{
            right: rightOffset,
            top: '50%',
            width: '25cqw',
            height: '60cqh',
            pointerEvents: visible ? 'auto' : 'none',
            // Same carved-stone material and radius as the social hub it
            // docks against, so the two panels read as one surface split
            // in half rather than as two different windows.
            ...OPAQUE_TEXTURED_PANEL_STYLE,
            borderRadius: 'var(--radius-2xl)',
          }}
        >
          <FourCorners color={`${GOLD} 0.55)`} size="1.7cqw" />
          <VikingOrnament ornament="broa016" variant="centerpiece" opacity={0.03} />

          <div className="flex-1 min-h-0 flex flex-col">
            {friend ? (
              <ChatView
                key={friend.friendId}
                ref={(ref) => { this.chatViewRef = ref; }}
                myUserId={profile?.id}
                friendId={friend.friendId}
                friendName={friend.friendName}
                friendAvatar={friend.friendAvatar}
                friendDisplayedCrownAvatarId={friend.friendDisplayedCrownAvatarId}
                friendIsFounder={!!friend.friendIsFounder}
                friendIsQaTester={!!friend.friendIsQaTester}
                online={friend.online}
                profile={profile}
                sorceryCards={sorceryCards}
                onBack={onClose}
                onHeaderClick={onViewProfile ? () => onViewProfile(friend.friendId) : undefined}
                onProfileReload={onProfileReload}
                onAcceptMatchInvite={onAcceptMatchInvite}
                onDeclineMatchInvite={onDeclineMatchInvite}
                onAcceptDraftInvite={onAcceptDraftInvite}
                onRejoinMatch={onRejoinMatch}
                onProfileUpdate={onProfileUpdate}
                onOpenCardPicker={() => this.setState({ showCardPicker: true })}
                matchHistory={this.props.matchHistory}
                onWatchReplay={this.props.onWatchReplay}
                onOpenTavernMessage={this.props.onOpenTavernMessage}
                onCardAttached={() => this.setState((s) => ({ attachVersion: s.attachVersion + 1 }))}
              />
            ) : null}
          </div>
        </motion.div>

        <CardPickerPanel
          open={open && !!friend && this.state.showCardPicker}
          right={pickerRightOffset}
          profile={profile}
          sorceryCards={sorceryCards}
          selectedCards={attachCards}
          onClose={() => this.setState({ showCardPicker: false })}
          onAddCard={(cardId, artGroup, foiling) => {
            this.chatViewRef?.addCard?.(cardId, artGroup, foiling);
            this.setState((s) => ({ attachVersion: s.attachVersion + 1 }));
          }}
          onRemoveCard={(cardId, artGroup, foiling) => {
            // ChatView.removeCard takes an index, not a card identity.
            // Find the first matching attach entry and remove it.
            // multiple copies share the same identity so removing
            // "one of the three" is the right user gesture.
            const list = this.chatViewRef?.state?.attachCards || [];
            const idx = list.findIndex((c) =>
              c.cardId === cardId
              && (c.artGroup || ART_GROUP_BASE) === artGroup
              && (c.foiling || 'S') === foiling
            );
            if (idx >= 0) this.chatViewRef?.removeCard?.(idx);
            this.setState((s) => ({ attachVersion: s.attachVersion + 1 }));
          }}
        />
      </>
    );
  }
}
